import { Inject, Injectable, InjectionToken } from '@angular/core';
import {
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import {
  KameleoonService,
  TOKENS as MASTERDATA_TOKENS,
} from '@trustedshops/tswp-core-masterdata';
import {
  reportMissingSlotTranslationKeys,
  resolveVariationName,
  selectSlotFieldConfig,
  UPGRADE_JOURNEY_FLAG,
  UpgradeJourneyContentConfig,
  UpgradeJourneyVariationConfig,
  UpgradeSlotConfig,
} from '../components/upgrade-journey-content';
import defaultUpgradeJourneyContent from './upgrade-journey-default-content.json';

/**
 * Injection token for the bundled default Upgrade Journey copy, transferred from
 * Kameleoon so the banner, page, and button always render sensible copy — even
 * when the visitor has not granted Kameleoon consent (Kameleoon then serves no
 * config). Defaults to the co-located `upgrade-journey-default-content.json`;
 * overridable (e.g. in tests) by providing the token.
 */
export const UPGRADE_JOURNEY_DEFAULT_CONTENT =
  new InjectionToken<UpgradeJourneyContentConfig>(
    'UPGRADE_JOURNEY_DEFAULT_CONTENT',
    {
      providedIn: 'root',
      factory: () =>
        defaultUpgradeJourneyContent as UpgradeJourneyContentConfig,
    }
  );

/**
 * A single slot field to resolve into its translation key.
 *
 * - `field` is the field name within the slot config (`chip`, `heading`,
 *   `paragraph`, `label`).
 * - `defaultKey` is the fallback translation key used when the resolved content
 *   config has no key for the field.
 */
export interface UpgradeSlotFieldRequest {
  field: string;
  defaultKey?: string;
}

/**
 * Reusable resolver for Upgrade Journey slot translation keys, shared by the
 * {@link ProductFeatureUpgradePageComponent}, the
 * {@link ProductFeatureUpgradeBannerComponent}, and the
 * {@link ProductFeatureUpgradeButtonComponent}.
 *
 * It centralises which translation key each slot field should use. Keys are
 * sourced from the bundled default config; Kameleoon only selects which
 * variation applies per slot:
 *
 * - Without Kameleoon consent (see {@link KameleoonService.hasConsent}) — or when
 *   Kameleoon selects no variation (absent or `default`) — the slot's `default`
 *   entry supplies the keys.
 * - With consent and a named variation, the slot's matching variation entry
 *   supplies the keys; a variation the bundled config has no entry for falls
 *   back to the slot's `default` entry.
 *
 * The service returns keys only — components bind them live in their templates
 * via `translationService.getTranslationForKey(key) | subscribe`, so the copy
 * re-renders once the (asynchronously loaded) i18n bundle resolves. A field with
 * no key (and no `defaultKey`) is reported to Instana.
 *
 * Not exported from the library barrel: it is an internal helper provided only
 * in the three upgrade components' modules and is not part of the public API.
 */
@Injectable()
export class ProductFeatureUpgradeTranslationService {
  public constructor(
    @Inject(MASTERDATA_TOKENS.KameleoonService)
    private readonly kameleoonService: KameleoonService,
    @Inject(COMMON_TOKENS.LogService)
    private readonly logService: LogService,
    @Inject(UPGRADE_JOURNEY_DEFAULT_CONTENT)
    private readonly defaultContent: UpgradeJourneyContentConfig
  ) {}

  /**
   * Resolves the translation key for each requested field of a slot.
   *
   * The key for every field is taken from the bundled default content for the
   * resolved variation (see class docs), falling back to the field's
   * `defaultKey`. A field that resolves to no key is reported via
   * {@link reportMissingSlotTranslationKeys} and returned as `null`.
   *
   * @param logSource - The reporting component (used as the Instana log source).
   * @param productFeatureKey - The feature whose copy is being resolved.
   * @param slot - The slot being rendered (`upgrade-banner`/`-page`/`-button`).
   * @param fields - The fields to resolve, with their default keys.
   * @returns A map of field name to resolved translation key (or `null`).
   */
  public async resolveSlotKeys(
    logSource: string,
    productFeatureKey: string,
    slot: string,
    fields: UpgradeSlotFieldRequest[]
  ): Promise<Record<string, string | null>> {
    const slotConfig = await this.resolveSlotConfig(productFeatureKey, slot);

    const resolutions = fields.map((field) => ({
      field: field.field,
      translationKey:
        slotConfig?.[field.field as keyof UpgradeSlotConfig]?.translation_key ??
        field.defaultKey ??
        null,
    }));

    // Report any field that resolved to no key (no content config, no default)
    // so the misconfiguration surfaces in Instana.
    reportMissingSlotTranslationKeys(
      this.logService,
      logSource,
      productFeatureKey,
      slot,
      resolutions
    );

    const result: Record<string, string | null> = {};
    resolutions.forEach((resolution) => {
      result[resolution.field] = resolution.translationKey;
    });
    return result;
  }

  /**
   * Resolves the slot's field config from the bundled default content, selecting
   * the variation via Kameleoon when consent has been granted.
   *
   * Without consent Kameleoon serves no config, so each slot's `default` entry
   * is used — this is the no-consent fallback that keeps copy from rendering
   * empty.
   */
  private async resolveSlotConfig(
    productFeatureKey: string,
    slot: string
  ): Promise<UpgradeSlotConfig | undefined> {
    let variationName: string | undefined;

    if (this.kameleoonService.hasConsent()) {
      // Each feature has its own JSON variable named after its key, so
      // experiments are configured per feature — a feature without an
      // experiment simply has no variable and falls back to the default copy.
      const variationConfig =
        await this.kameleoonService.getFeatureVariable<UpgradeJourneyVariationConfig>(
          UPGRADE_JOURNEY_FLAG,
          productFeatureKey?.toLowerCase()
        );
      variationName = resolveVariationName(variationConfig, slot);
    }

    return selectSlotFieldConfig(
      this.defaultContent,
      productFeatureKey,
      slot,
      variationName
    );
  }
}
